import * as React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@replo/design-system/components/button/Button";
import { Field, FieldError } from "@replo/design-system/components/field/Field";
import { Input } from "@replo/design-system/components/input/Input";
import twMerge from "@replo/design-system/utils/twMerge";
import { Box } from "lucide-react";
import { useForm } from "react-hook-form";
import { isValidDomain } from "replo-utils/lib/url";
import { z } from "zod";

import { SkeletonPreviewPlaceholder } from "./SkeletonPreviewPlaceholder";

const urlFormSchema = z.object({
  url: z
    .string()
    .min(1, "URL is required")
    .refine(
      (val) => {
        if (val.startsWith("http://") || val.startsWith("https://")) {
          try {
            const parsed = new URL(val);
            return isValidDomain(parsed.hostname);
          } catch {
            return false;
          }
        }
        return isValidDomain(val);
      },
      { message: "Please enter a valid URL" },
    ),
});

type UrlFormData = z.infer<typeof urlFormSchema>;

interface CustomStylesInputCardProps {
  isSelected: boolean;
  onSelect: () => void;
  hasBrandData: boolean;
  onBrandPullStyles: () => void;
  onUrlPullStyles: (url: string) => void;
  brandColors: string[];
  brandFonts: { heading: string; body: string };
  logoUrl: string | null;
  brandName?: string | null;
}

export function CustomStylesInputCard({
  isSelected,
  onSelect,
  hasBrandData,
  onBrandPullStyles,
  onUrlPullStyles,
  brandColors,
  brandFonts,
  logoUrl,
  brandName,
}: CustomStylesInputCardProps) {
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch,
  } = useForm<UrlFormData>({
    resolver: zodResolver(urlFormSchema),
    mode: "onChange",
    defaultValues: { url: "" },
  });

  const urlValue = watch("url");

  const onSubmit = (data: UrlFormData) => {
    onUrlPullStyles(data.url);
  };

  return (
    <div
      onClick={onSelect}
      role="button"
      tabIndex={0}
      aria-label="Select custom styles"
      className={twMerge(
        "border-0.5 border-border flex flex-col gap-0 rounded-xl p-0 shadow-md overflow-hidden cursor-pointer w-full md:w-75 shrink-0 h-[356px] transition-all duration-200",
        isSelected && "border-[0.8px] border-black",
      )}
    >
      <div className="flex flex-col gap-0 p-0 flex-1 overflow-hidden relative bg-surface">
        <SkeletonPreviewPlaceholder />

        <div className="absolute top-4 left-3 bg-white border-0.5 border-border p-3 rounded-xl shadow-2xs">
          <div className="flex items-center h-10 w-48 isolate pr-4">
            {brandColors.map((color, index) => {
              const zIndexClasses = [
                "z-[6]",
                "z-[5]",
                "z-[4]",
                "z-[3]",
                "z-[2]",
                "z-[1]",
              ];
              return (
                <div
                  key={index}
                  className={`flex-1 h-full shrink-0 -mr-4 rounded-lg border-0.5 border-black/10 ${zIndexClasses[index]}`}
                  style={{ backgroundColor: color }}
                />
              );
            })}
          </div>
        </div>

        {hasBrandData ? (
          <div className="absolute top-28 right-1.5 max-w-[calc(100%-1.5rem)] bg-white border-0.5 border-border p-3 rounded-xl shadow-2xs">
            <div className="flex items-center gap-1.5">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt="Brand logo"
                  className="size-5 shrink-0 object-contain"
                />
              ) : (
                <Box className="size-5 shrink-0" strokeWidth={2} />
              )}
              <span className="text-base font-bold tracking-wide truncate">
                {logoUrl ? brandName ?? "Logo" : "Logo"}
              </span>
            </div>
          </div>
        ) : (
          <div className="absolute top-24 -right-6 bg-white border-0.5 border-border p-3 rounded-lg shadow-2xs">
            <div className="flex gap-4">
              <div className="flex flex-col gap-1">
                <span className="text-2xs font-medium text-placeholder">
                  Headings
                </span>
                <span className="text-sm font-bold leading-3.5">
                  {brandFonts.heading}
                </span>
              </div>
              <div className="flex flex-col gap-1 w-23">
                <span className="text-2xs font-medium text-placeholder">
                  Body
                </span>
                <span className="text-xs leading-3.5">{brandFonts.body}</span>
              </div>
            </div>
          </div>
        )}

        {!hasBrandData && (
          <div className="absolute top-44 left-3 bg-white border-0.5 border-border p-3 rounded-xl shadow-2xs">
            <div className="flex items-center gap-1.5">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt="Brand logo"
                  className="size-5 object-contain"
                />
              ) : (
                <Box className="size-5" strokeWidth={2} />
              )}
              <span className="text-base font-bold tracking-wide">
                {logoUrl ? brandName ?? "Logo" : "Logo"}
              </span>
            </div>
          </div>
        )}

        <div className="absolute bottom-0 inset-x-0 h-17 bg-gradient-to-t from-white to-transparent pointer-events-none" />
      </div>

      <div className="flex flex-col items-start gap-3 p-4 bg-white relative z-10">
        <div className="flex flex-col gap-0.5">
          <span className="text-base font-semibold text-default">
            {hasBrandData
              ? "Use your own brand"
              : "Looking for something specific?"}
          </span>
          <span className="text-sm text-muted">
            {hasBrandData
              ? "We'll pull in your colors, fonts, images, and logo."
              : "Pull brand styles from any URL."}
          </span>
        </div>
        {hasBrandData ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onBrandPullStyles();
            }}
            className="w-full"
          >
            Pull styles
          </Button>
        ) : (
          <form
            className="flex items-center gap-3 w-full"
            onSubmit={(e) => {
              e.stopPropagation();
              void handleSubmit(onSubmit)(e);
            }}
          >
            <Field size="sm" className="flex-1">
              <Input
                {...register("url")}
                placeholder="Enter URL"
                size="sm"
                invalid={Boolean(errors.url)}
                onClick={(e) => e.stopPropagation()}
              />
              <FieldError size="sm">{errors.url?.message}</FieldError>
            </Field>
            <Button
              variant="secondary"
              size="sm"
              type="submit"
              disabled={!urlValue || !isValid}
              onClick={(e) => e.stopPropagation()}
            >
              Pull styles
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
